/**
 * Shared filters for Datadog RUM and Browser Logs ingestion.
 * Drops known third-party, extension, and analytics noise before events are sent.
 */

const MESSAGE_DENYLIST: RegExp[] = [
	/resizeobserver loop/i,
	/object not found matching id/i,
	/failed to connect to metamask/i,
];

/** Hostnames / domains for scripts and requests we do not own. */
const DOMAIN_DENYLIST = [
	"contentsquare.net",
	"heap-api.com",
	"googletagmanager.com",
	"google-analytics.com",
];

const EXTENSION_PATTERNS: RegExp[] = [
	/chrome-extension:\/\//i,
	/moz-extension:\/\//i,
	/safari-web-extension:\/\//i,
];

/** Aligns with first-party redirect trust in src/contexts/auth/utils.ts */
const FIRST_PARTY_INDICATORS: RegExp[] = [
	/usefoam\.com/i,
	/whalar\.com/i,
	/foam\.io/i,
	/foam\.local/i,
	/localhost/i,
	/\/_next\//i,
	/webpack-internal:/i,
];

const STACK_URL_PATTERN =
	/(?:https?|chrome-extension|moz-extension|safari-web-extension):\/\/[^\s)]+/gi;

type TelemetryPayload = {
	message?: string;
	stack?: string;
	url?: string;
};

function combineText(...parts: (string | undefined)[]): string {
	return parts.filter(Boolean).join("\n");
}

function matchesMessageDenylist(message: string | undefined): boolean {
	if (!message) return false;
	return MESSAGE_DENYLIST.some((pattern) => pattern.test(message));
}

function matchesDomainDenylist(text: string): boolean {
	const lower = text.toLowerCase();
	return DOMAIN_DENYLIST.some((domain) => lower.includes(domain));
}

function matchesExtensionPattern(text: string): boolean {
	return EXTENSION_PATTERNS.some((pattern) => pattern.test(text));
}

function matchesMetaMask(text: string): boolean {
	return /metamask/i.test(text);
}

function extractStackUrls(stack: string): string[] {
	return stack.match(STACK_URL_PATTERN) ?? [];
}

function hasFirstPartyIndicator(text: string): boolean {
	return FIRST_PARTY_INDICATORS.some((pattern) => pattern.test(text));
}

function isThirdPartyStackOnly(stack: string | undefined): boolean {
	if (!stack) return false;
	const urls = extractStackUrls(stack);
	if (urls.length === 0) return false;
	const hasFirstPartyFrame =
		urls.some((url) => hasFirstPartyIndicator(url)) ||
		hasFirstPartyIndicator(stack);
	return !hasFirstPartyFrame;
}

/**
 * Returns true when the error/log should not be sent to Datadog.
 */
export function shouldDiscardTelemetry(payload: TelemetryPayload): boolean {
	const combined = combineText(payload.message, payload.stack, payload.url);
	if (!combined) {
		return false;
	}

	if (matchesMessageDenylist(payload.message)) {
		return true;
	}

	if (matchesDomainDenylist(combineText(payload.message, payload.url))) {
		return true;
	}

	if (matchesExtensionPattern(combined)) {
		return true;
	}

	if (matchesMetaMask(combined)) {
		return true;
	}

	if (isThirdPartyStackOnly(payload.stack)) {
		return true;
	}

	return false;
}

type RumEvent = {
	type: string;
	error?: {
		message?: string;
		stack?: string;
		resource?: { url?: string };
	};
};

type RumErrorContext = {
	error?: { stack?: string };
};

export function shouldDiscardRumEvent(
	event: RumEvent,
	context?: unknown,
): boolean {
	if (event.type !== "error") {
		return false;
	}

	const errorContext = context as RumErrorContext | undefined;
	return shouldDiscardTelemetry({
		message: event.error?.message,
		stack: event.error?.stack ?? errorContext?.error?.stack,
		url: event.error?.resource?.url,
	});
}

type BrowserLog = {
	message?: string;
	http?: { url?: string };
	error?: { message?: string; stack?: string };
};

export function shouldDiscardBrowserLog(log: BrowserLog): boolean {
	return shouldDiscardTelemetry({
		message: log.message ?? log.error?.message,
		stack: log.error?.stack,
		url: log.http?.url,
	});
}
